import { useEffect, useRef, useState } from 'react';

/**
 * Milliseconds until `deadline`, re-evaluated every second. Stops ticking
 * once the deadline passes and calls `onExpired` exactly once per deadline
 * (the caller refetches the discount so the next phase's deadline, if any,
 * takes over).
 */
export const useDeadlineCountdown = (
  deadline: Date,
  onExpired?: () => void
): number => {
  const deadlineMs = deadline.getTime();
  const getRemaining = () => deadlineMs - Date.now();
  const [remaining, setRemaining] = useState(getRemaining);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    let interval: number | undefined;

    const tick = () => {
      const nextRemaining = deadlineMs - Date.now();
      setRemaining(nextRemaining);

      if (nextRemaining <= 0) {
        if (interval !== undefined) {
          window.clearInterval(interval);
          interval = undefined;
        }
        onExpiredRef.current?.();
        return false;
      }

      return true;
    };

    if (!tick()) return;

    interval = window.setInterval(tick, 1000);

    return () => {
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, [deadlineMs]);

  return remaining;
};
